<template>
  <div v-if="!pageReady">
    <b-spinner label="Loading"></b-spinner>
  </div>

  <div v-else class="page-container">
    <!-- Content inside the page-container -->
    <b-alert :show="Boolean(this.pageError)" variant="danger">
      {{ this.pageError }}
    </b-alert>

    <img class="margin-top-medium" alt="Teleportal Session Finished" src="https://teleportal-files.s3-ap-southeast-2.amazonaws.com/endsession.png" />

    <h3 class="subtitle">SESSION SUMMARY</h3>


    <div class="session-container">
      <div class="information">
        <strong><p>Total Minutes:</p></strong>
        <strong><p>Total Credits:</p></strong>
        <strong><p>Session ID:</p></strong>
        <strong><p>Start Time:</p></strong>
      </div>

      <div class="information2" v-if="currentSession">
        <p>{{ sessionTimeInMinutes }}</p>
        <p>{{ sessionCost }}</p>
        <p>{{ currentSession.id }}</p>
        <p>{{ formatDate(currentSession.start_date) }}</p>
      </div>
    </div>

    <h4 class="margin-top-big">We hope you enjoyed your robotic adventure with us!</h4>

    <h5 class="margin-top-big">
      If you took any photos, you can view them in our
      <router-link to="/gallery">Gallery</router-link>.
    </h5>

    <h5 class="margin-top-medium">
      You can watch the video recording of your session on our
      <a
        href="https://www.youtube.com/channel/UCm9KWzWfh7wMV5i2QPSaWWw/videos"
        target="_blank"
      >
        YouTube Channel.</a>
    </h5>
    <p class="margin-top-small">
      It can take up to five minutes for your robot session video to be available.
    </p>

    <h5 class="margin-top-medium">
      Learn more about the animals you have seen by visiting our
      <a href="https://teleportal.net/dive-sites" target="_blank">
        Dive Site Information.</a>
    </h5>

    <h5 class="margin-top-medium">Teleportal is brand new and we would love to hear your feedback.</h5>
    
    <div class="button-container margin-top-large">
      <b-button
        class="button"
        href="https://feedback.teleportal.net/"
        pill
        variant="primary"
      >Submit Feedback</b-button>

      <router-link to="/robots">
        <b-button class="button" pill variant="primary">Available Robots</b-button>
      </router-link>

      <router-link to="/gallery">
        <b-button class="button" pill variant="primary">Photo Gallery</b-button>
      </router-link>

      <b-button
        class="button"
        href="https://www.youtube.com/channel/UCm9KWzWfh7wMV5i2QPSaWWw/videos"
        pill
        variant="primary"
      >YouTube Channel</b-button>
    </div>

  </div>
</template>

<script>
import Rovers from "@/services/api/controllers/rovers";
import Sessions from "@/services/api/controllers/sessions";

const MINUTE_IN_MILLISECONDS = 60000;

export default {
  data: function() {
    return {
      currentSession: null,
      pageError: "",
      pageReady: true,
      sessionCost: null,
      sessionTimeInMinutes: null,
      creditsDeducted: false
    };
  },

  methods: {
    // Converts milliseconds to minutes
    getSessionTimeInMinutes: function(startTime, endTime) {
      return Math.ceil(
        (endTime.getTime() - startTime.getTime()) / MINUTE_IN_MILLISECONDS
      );
    },
    formatDate: function(dateString) {
      const date = new Date(dateString);
      date.setHours(date.getHours() + 10);  // Adjust for Brisbane GMT+10

      return new Intl.DateTimeFormat('en-AU', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    }
  },
  

  mounted: async function() {
    try {
      this.pageReady = false;
      let sessionCost;

      if (!this.creditsDeducted) {
        // Get the current sessions
        const accessToken = await this.$auth.getTokenSilently();
        this.currentSession = await Sessions.showMostRecent(
          this.$route.params.id,
          accessToken
        );

        // Get start time
        const startTimeConverted = new Date(this.currentSession.start_date);

        // Get the real current time to approximate the end time of the sessions
        const endTimeConverted = new Date();

        this.sessionTimeInMinutes = this.getSessionTimeInMinutes(
          startTimeConverted,
          endTimeConverted
        );

        // Update the credits on the front end
        // Get the rover information
        const currentRover = await Rovers.show(this.$route.params.roverID);

        if (this.sessionTimeInMinutes <= 0) {
          // Case 1: The session never started, do not charge the user
          sessionCost = 0;
          this.sessionTimeInMinutes = 0;
        } else {
          sessionCost = Math.ceil(
            this.sessionTimeInMinutes * currentRover.credits_per_minute
          );
        }

        this.sessionCost = sessionCost;

        this.$auth.credits =
          this.$auth.credits - sessionCost < 0
            ? 0
            : this.$auth.credits - sessionCost;
        this.creditsDeducted = true;
      }
    } catch (error) {
      if (error.error && error.error === "login_required") {
        this.pageError =
          "WARNING: We had troubles grabbing your session information. Sit tight as your session will be ended without overcharging";
      } else {
        this.pageError = error.message;
      }
    } finally {
      this.pageReady = true;
    }
  }
};
</script>

<style scoped>
.page-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.session-container {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 75%;
  border: none;
  flex-wrap: nowrap;
  margin-top: 8rem;
  margin-bottom: 8rem;
}

.information {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  font-size: 1.2rem;
  color: black; /* Make text black */
}

.information2 {
  flex: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: black; /* Make text black */
}

.page-container img {
  max-width: 75%; /* Adjust the image size */
  height: auto;
  margin-top: 4rem;
  margin-bottom: 4rem;
}

.subtitle, h4, h5, p {
  text-align: center;
  color: black; /* Make text black */
}

h5 a, .router-link {
  color: #03a9f4; /* Set link color */
}

.button {
  margin: 0.5rem;
  display: inline-block; /* Make buttons inline */
}

.margin-top-small {
  margin-top: 1rem;  /* Adjust the value to suit your design */
}
.margin-top-medium {
  margin-top: 2rem;
}
.margin-top-large {
  margin-top: 4rem;
}
.margin-top-big {
  margin-top: 8rem;
}

.button-container {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  justify-content: center;
  margin-top: 4rem; 
  margin-bottom: 4rem;
}

/* Media Query for tablet screens */
@media (max-width: 768px) {
  .session-container {
    
  }

  .page-container img {
    max-width: 100%;
  }

  .button {
    display: block;
    width: 100%;
  }
}

/* Media Query for mobile screens */
@media (max-width: 480px) {
  .information {
    align-items: center;
    padding: 0 10px;
  }
}
</style>

